import {
  type EventDto,
  fetchEventAddFacilitator,
  fetchEventAddRsvp,
  fetchEventAddToEvent,
  fetchEventChangeOwnership,
  fetchEventDeleteEvent,
  fetchEventDeleteFacilitator,
  fetchEventDenyRequest,
  fetchEventGetEvent,
  fetchEventLeaveEvent,
  fetchEventRemoveFromEvent,
  fetchEventRemoveRsvp,
  fetchEventRequestAddToEvent,
  fetchEventUpdateMeetingTime,
  fetchEventUploadEventPicture,
  type UpdateMeetingTimeDto,
} from "@crowdcoursing/api/openapi";
import { createAsyncThunk } from "@reduxjs/toolkit";

import { assertNonEmptyString, assertNotUndefined } from "~utils/assert";

import { errorToast, successToast } from "../state/notifications";

const tSuccessMessageUpdateMeetingTime = "Updated Event!";
const tErrorMessageUpdateMeetingTime = "Failed to update Event";
const tDefaultErrorMessage = "Whoops! Sorry about that. Please try again.";
const tRsvpSucessMessage = "Successfully rsvped!";
const tUnRsvpSuccessMessage = "Successfully un-rsvped!";

/** @deprecated We are phasing out `redux` */
export const updateMeetingTime = createAsyncThunk(
  "sessions/updateMeetingTime",
  async (
    { body, ...pathParams }: { meetingId: string; eventId: string; body?: UpdateMeetingTimeDto },
    { dispatch },
  ) => {
    assertNonEmptyString(pathParams?.eventId);
    assertNotUndefined(body);
    try {
      await fetchEventUpdateMeetingTime({ body, pathParams });
      dispatch(successToast({ message: tSuccessMessageUpdateMeetingTime }));
      return await fetchEventGetEvent({ pathParams });
    } catch (exception: unknown) {
      window.console.error(exception);
      dispatch(errorToast({ message: tErrorMessageUpdateMeetingTime }));
    }
  },
);

/** @deprecated We are phasing out `redux` */
export const requestMembership = createAsyncThunk(
  "app/requestMembershipSession",
  async (pathParams: { eventId: string; userId: string }, { dispatch }) => {
    try {
      await fetchEventRequestAddToEvent({ pathParams });
      // ! Figure out why the return type here is `undefined`
      // updated pending request Ids are in this heavy data fetching endpoint
      const ccEvent = (await fetchEventGetEvent({ pathParams })) as unknown as EventDto;
      if (ccEvent.isPublic) {
        dispatch(successToast({ message: "Event joined successfully!" }));
      } else {
        dispatch(successToast({ message: "Event request sent successfully!" }));
      }
      return ccEvent;
    } catch (error) {
      window.console.log("error: ", JSON.stringify(error, undefined, 2));
      dispatch(errorToast({ message: tDefaultErrorMessage }));
    }
  },
);

/** @deprecated We are phasing out `redux` */
export const addMember = createAsyncThunk(
  "app/addMembershipSession",
  async (pathParams: { eventId: string; userId: string }, { dispatch }) => {
    try {
      await fetchEventAddToEvent({ pathParams });
      dispatch(successToast({ message: "Member added successfully!" }));
    } catch (error) {
      window.console.log("error: ", JSON.stringify(error, undefined, 2));
      dispatch(errorToast({ message: tDefaultErrorMessage }));
    }
  },
);

/** @deprecated We are phasing out `redux` */
export const rejectMember = createAsyncThunk(
  "app/rejectMembershipSession",
  async (pathParams: { roomId: string; eventId: string; userId: string }, { dispatch }) => {
    try {
      await fetchEventDenyRequest({ pathParams });
      dispatch(successToast({ message: "Member request rejected." }));
    } catch (error) {
      window.console.log("error: ", JSON.stringify(error, undefined, 2));
      dispatch(errorToast({ message: tDefaultErrorMessage }));
    }
  },
);

/** @deprecated We are phasing out `redux` */
export const removeMember = createAsyncThunk(
  "app/removeMemberSession",
  async (pathParams: { eventId: string; userId: string }, { dispatch }) => {
    try {
      await fetchEventRemoveFromEvent({ pathParams });
      dispatch(successToast({ message: "Member removed successfully!" }));
    } catch (error) {
      window.console.log("error: ", JSON.stringify(error, undefined, 2));
      dispatch(errorToast({ message: tDefaultErrorMessage }));
    }
  },
);

/** @deprecated We are phasing out `redux` */
export const addFacilitator = createAsyncThunk(
  "app/addFacilitatorEvent",
  async (pathParams: { eventId: string; userId: string }, { dispatch }) => {
    try {
      await fetchEventAddFacilitator({ pathParams });
      dispatch(successToast({ message: "Facilitator added!" }));
    } catch (exception: unknown) {
      window.console.log("exception: ", JSON.stringify(exception, undefined, 2));
      dispatch(errorToast({ message: tDefaultErrorMessage }));
    }
  },
);

/** @deprecated We are phasing out `redux` */
export const removeFacilitator = createAsyncThunk(
  "app/removeFacilitatorEvent",
  async (pathParams: { eventId: string; userId: string }, { dispatch }) => {
    try {
      await fetchEventDeleteFacilitator({ pathParams });
      dispatch(successToast({ message: "Facilitator permissions removed successfully." }));
    } catch (exception: unknown) {
      window.console.log("exception: ", JSON.stringify(exception, undefined, 2));
      dispatch(errorToast({ message: tDefaultErrorMessage }));
    }
  },
);

/** @deprecated We are phasing out `redux` */
export const leave = createAsyncThunk(
  "app/leaveSession",
  async (pathParams: { eventId: string; userId: string }, { dispatch }) => {
    try {
      await fetchEventLeaveEvent({ pathParams });
      dispatch(successToast({ message: "Left event successfully!" }));
    } catch (error) {
      window.console.log("error: ", JSON.stringify(error, undefined, 2));
      dispatch(errorToast({ message: tDefaultErrorMessage }));
    }
  },
);

/** @deprecated We are phasing out `redux` */
export const rsvp = createAsyncThunk(
  "meeting/rsvp",
  async (pathParams: { eventId: string; meetingId: string }, { dispatch }) => {
    try {
      await fetchEventAddRsvp({ pathParams });
      dispatch(successToast({ message: tRsvpSucessMessage }));
    } catch (error) {
      window.console.log("error: ", JSON.stringify(error, undefined, 2));
      dispatch(errorToast({ message: tDefaultErrorMessage }));
    }
  },
);

/** @deprecated We are phasing out `redux` */
export const deleteEvent = createAsyncThunk(
  "sessions/deleteEvent",
  async (pathParams: { eventId: string }, { dispatch }) => {
    await fetchEventDeleteEvent({ pathParams });
    dispatch(successToast({ message: "Event deleted successfully!" }));
    return { eventId: pathParams.eventId };
  },
);

/** @deprecated We are phasing out `redux` */
export const deleteRsvp = createAsyncThunk(
  "meeting/deleteRsvp",
  async (pathParams: { eventId: string; meetingId: string }, { dispatch }) => {
    try {
      await fetchEventRemoveRsvp({ pathParams });
      dispatch(successToast({ message: tUnRsvpSuccessMessage }));
    } catch (error) {
      dispatch(errorToast({ message: tDefaultErrorMessage }));
      window.console.log("error: ", JSON.stringify(error, undefined, 2));
    }
  },
);

/** @deprecated We are phasing out `redux` */
export const saveEventPicture = createAsyncThunk(
  "app/saveEventPicture",
  async ({ formFile: FormFile, ...pathParams }: { eventId: string; formFile: File }) => {
    await fetchEventUploadEventPicture({ body: { FormFile }, pathParams });
    return { eventId: pathParams.eventId };
  },
);

/** @deprecated We are phasing out `redux` */
export const addOwner = createAsyncThunk(
  "app/addOwnerEvent",
  async (
    { body: pathParams, onSuccess }: { body: { eventId: string; userId: string }; onSuccess?: () => void },
    { dispatch },
  ) => {
    try {
      await fetchEventChangeOwnership({ pathParams });
      dispatch(successToast({ message: "Ownership changed successfully!" }));
      onSuccess?.();
    } catch (exception: unknown) {
      window.console.log("exception: ", JSON.stringify(exception, undefined, 2));
      dispatch(errorToast({ message: tDefaultErrorMessage }));
    }
  },
);
